// « Ma gamme » (option activée par l'éditeur) : types et petits composants communs aux écrans.
// Le serveur ne transmet le bloc `achats` (fabricant, fournisseur, prix d'achat) et les accords qu'aux rôles qui voient les coûts :
// l'interface n'affiche que ce qu'elle reçoit, rien n'est masqué en CSS.
import { Badge } from '../../components/ui.tsx';
import { LIBELLES_NATURES, LIBELLES_PROTOCOLES, type NatureProduit, type Protocole } from '@shared/gamme.ts';

export interface Achats {
  fabricant: string | null; reference_fabricant: string | null; fournisseur: { id: string; nom: string | null } | null; prix_achat: string | null;
  prix_effectif: { prix: string | null; origine: string | null; detail: string; palier_min: string | null } | null;
}
export interface Produit {
  id: string; nature: NatureProduit; designation: string; reference: string; marque: string | null; famille: string | null;
  description: string | null; caracteristiques: { libelle: string; valeur: string }[]; protocoles: Protocole[]; unite: string;
  prix_vente_conseille?: string | null; stock: string; actif: boolean; demo: boolean; version: number; achats?: Achats;
}
/** Réglages de Ma gamme servis aux rôles qui voient les accords (gamme.achats) ; null pour les autres. */
export interface ReglagesGamme { alerte_accord_jours: number; defaut: number; min: number; max: number; regler: boolean }
/** « 30 jours » / « 1 jour » : délai d'alerte réglé par la société, jamais une valeur écrite en dur. */
export const texteDelai = (n: number) => `${n} jour${n > 1 ? 's' : ''}`;

export interface AlerteAccord { produit_id: string; designation: string; reference: string; etat: 'fin_proche' | 'echu'; date_fin: string; jours: number }
export interface AlerteConformite { produit_id: string; designation: string; reference: string; manquants: string[]; expires: string[]; a_renouveler: string[] }

export function BadgeNature({ nature }: { nature: NatureProduit }) {
  return <Badge ton={nature === 'maison' ? 'accent' : 'info'} icone={false}>{LIBELLES_NATURES[nature]}</Badge>;
}
export function Protocoles({ liste }: { liste: Protocole[] }) {
  if (!liste.length) return <span className="tres-petit discret">Aucune compatibilité renseignée</span>;
  return <span className="mg-protocoles">{liste.map((p) => <span key={p} className="mg-protocole">{LIBELLES_PROTOCOLES[p] ?? p}</span>)}</span>;
}
export function texteAlerteAccord(a: { etat: string; jours: number; date_fin: string }, date: (v: string) => string) {
  return a.etat === 'echu' ? `Accord échu depuis le ${date(a.date_fin)}`
    : a.jours === 0 ? 'Accord qui finit aujourd’hui' : `Accord qui finit dans ${a.jours} jour${a.jours > 1 ? 's' : ''} (${date(a.date_fin)})`;
}
/** Encart discret, replié : le titre dit l'essentiel, le détail s'ouvre au toucher (le décor cède la place à la fonction). */
export function EncartFabricant() {
  return (
    <details className="mg-encart petit">
      <summary>Mettre votre marque sur un produit fait de votre société son fabricant : ce que cela implique</summary>
      <p>{ENCART_FABRICANT}</p>
    </details>
  );
}
/** Encart d'information discret sur un produit maison (sources : docs/REGLEMENTATION-MA-GAMME.md). */
export const ENCART_FABRICANT = 'Mettre votre marque sur un produit fait de votre société son fabricant : vous portez la déclaration UE de conformité, '
  + 'la documentation technique, le marquage CE, l’identification du produit (votre nom et adresse postale) et la notice en français. '
  + 'L’attestation d’éco-organisme (DEEE / REP) est facultative dans TRAME : joignez-la si vous l’avez, aucune alerte ne la réclame. '
  + 'TRAME vous aide à suivre ces documents et leurs échéances ; il ne remplace ni l’évaluation de la conformité ni un conseil juridique.';
